import { ServiceWorkerUpdateBanner } from './components/ServiceWorkerUpdateAction';
import ClinicalTimeResolutionDialog from './components/ClinicalTimeResolutionDialog';
import React, { useEffect, useState } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { MessageProvider, setNotificationCallback } from "./contexts/MessageContext";
import { NotificationProvider, useNotifications } from "./contexts/NotificationContext";
import { ServiceWorkerProvider } from "./contexts/ServiceWorkerContext";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import { AppointmentTypeCatalogProvider } from "./contexts/AppointmentTypeCatalogContext";
import { SystemNotificationProvider, useSystemNotification } from "./contexts/SystemNotificationContext";
import PaywallModal from "./components/billing/PaywallModal";
import SubscriptionBanner from "./components/billing/SubscriptionBanner";
import { queryClient } from "./utils/queryClient";
import AppRoutes from "./AppRoutes";
import OfflineBanner from "./components/OfflineBanner";
import QueueAuthGate from "./components/QueueAuthGate";
import SessionExpiredModal from "./components/SessionExpiredModal";
import QueuedToastListener from "./components/QueuedToastListener";
import NotificationList from "./components/notifications/NotificationList";
import NotificationPermissionBanner from "./components/notifications/NotificationPermissionBanner";
import { initializeNotifications } from "./utils/notifications/initNotifications";
import ErrorBoundary from "./components/ErrorBoundary";
import SystemNotificationBanner from "./components/SystemNotificationBanner";
import ClientSystemNotificationBanner from "./components/ClientSystemNotificationBanner";
import AnnouncementBanner from "./components/announcements/AnnouncementBanner";
import { isPublicStandalonePath } from "./utils/publicStandaloneRoutes";
import OfflineChartWorkspace from './components/OfflineChartWorkspace';
import MileageEntryModal from './components/mileage/MileageEntryModal';
import mileageApi from './utils/api/mileageApi';
import { MILEAGE_PROMPT_EVENT } from './utils/mileageEvents';
import PracticeContextSwitcher from './components/PracticeContextSwitcher';
import BackToLaborButton from './components/BackToLaborButton';
import BillingWorkspaceShell from './auth/BillingWorkspaceShell';
import { isBillingContext, isClientRole, isContextSelectionRequired, isGlobalAdminContext } from './auth/roleUtils';
import { removeLegacyIntakeDraftLocalStorage } from './utils/legacyIntakeDraftCleanup';

const SW_REFRESHED_KEY = 'vela:sw-refreshed';

function AccountActivationRedirect() {
  const { logout } = useAuth();

  useEffect(() => {
    const handleActivationRequired = () => logout();
    window.addEventListener('vela:account-activation-required', handleActivationRequired);
    return () => window.removeEventListener('vela:account-activation-required', handleActivationRequired);
  }, [logout]);

  return null;
}

function UpdateRefreshedModal({ message, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="update-refreshed-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="update-refreshed-title" className="text-lg font-semibold text-gray-900">
          Sena has been updated
        </h2>
        <p className="mt-2 text-sm text-gray-600">
          We refreshed the app so you&apos;re on the latest version.
        </p>
        {message && (
          <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">
              What changed
            </p>
            <p className="mt-1 text-sm text-blue-950">{message}</p>
          </div>
        )}
        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-[#4f7a64] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#3d6350]"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

// Inner component to access notification context
function AppContent() {
  const { isAuthenticated, user } = useAuth();
  const { showNotification, dismissNotification, notifications } = useNotifications();
  const { notification, isFetched: systemNotificationFetched } = useSystemNotification();
  const [showUpdateRefreshedModal, setShowUpdateRefreshedModal] = useState(false);
  const [mileagePrompt, setMileagePrompt] = useState(null);
  const [mileagePreview, setMileagePreview] = useState(null);
  const role = user?.role || user?.userRole;
  const isClientUser = isClientRole(role);
  const hasWorkspaceSession = isAuthenticated && !!user && !isContextSelectionRequired(user);
  const hasPracticeWorkspace = hasWorkspaceSession && !isGlobalAdminContext(user);
  const isMidwifeUser = hasPracticeWorkspace && !isClientUser;
  const isPublicStandalonePage = isPublicStandalonePath();

  useEffect(() => {
    if (!isAuthenticated) return;
    removeLegacyIntakeDraftLocalStorage();
  }, [isAuthenticated]);

  useEffect(() => {
    // Set up the notification callback for MessageContext
    setNotificationCallback(showNotification);

    return () => {
      setNotificationCallback(null);
    };
  }, [showNotification]);

  useEffect(() => {
    if (!hasWorkspaceSession) return undefined;
    let disposed = false;
    let cleanupMonitoring;
    initializeNotifications().then((cleanup) => {
      if (disposed) cleanup?.();
      else cleanupMonitoring = cleanup;
    }).catch((error) => console.error('Failed to initialize notifications:', error));
    return () => { disposed = true; cleanupMonitoring?.(); };
  }, [hasWorkspaceSession, user?.sub, user?.contextReferenceId]);

  useEffect(() => {
    if (!systemNotificationFetched) return;

    try {
      const refreshedAt = sessionStorage.getItem(SW_REFRESHED_KEY);
      if (!refreshedAt) return;
      sessionStorage.removeItem(SW_REFRESHED_KEY);
      if (isPublicStandalonePage) return;
      setShowUpdateRefreshedModal(true);
    } catch {
      // If storage is blocked, skip the modal rather than blocking app startup.
    }
  }, [isPublicStandalonePage, systemNotificationFetched]);

  useEffect(() => {
    if (isPublicStandalonePage || !isMidwifeUser) return undefined;

    let cancelled = false;

    const handleMileagePrompt = async (event) => {
      const detail = event?.detail || {};
      if (!detail.sourceType || !detail.sourceReferenceId) return;

      try {
        const preview = await mileageApi.getSourcePreview(detail);
        if (cancelled) return;
        setMileagePrompt(detail);
        setMileagePreview(preview);
      } catch (error) {
        console.warn('Mileage prompt unavailable:', error);
      }
    };

    window.addEventListener(MILEAGE_PROMPT_EVENT, handleMileagePrompt);
    return () => {
      cancelled = true;
      window.removeEventListener(MILEAGE_PROMPT_EVENT, handleMileagePrompt);
    };
  }, [isPublicStandalonePage, isMidwifeUser]);

  return (
    <>
      <AccountActivationRedirect />
      {isAuthenticated && !isPublicStandalonePage && <PracticeContextSwitcher />}
      {!isPublicStandalonePage && hasPracticeWorkspace && (isMidwifeUser ? <SystemNotificationBanner /> : <ClientSystemNotificationBanner />)}
      {!isPublicStandalonePage && isMidwifeUser && <AnnouncementBanner />}
      <OfflineBanner />
      {!isPublicStandalonePage && <ServiceWorkerUpdateBanner />}
      <ClinicalTimeResolutionDialog />
      {!isPublicStandalonePage && isMidwifeUser && <SubscriptionBanner />}
      {isMidwifeUser && <PaywallModal />}
      {!isPublicStandalonePage && <QueueAuthGate />}
      {!isPublicStandalonePage && <SessionExpiredModal />}
      <QueuedToastListener />
      {!isPublicStandalonePage && <OfflineChartWorkspace />}
      <NotificationList 
        notifications={notifications} 
        onDismiss={dismissNotification} 
      />
      {showUpdateRefreshedModal && (
        <UpdateRefreshedModal
          message={notification?.message}
          onClose={() => setShowUpdateRefreshedModal(false)}
        />
      )}
      {mileagePrompt && mileagePreview && (
        <MileageEntryModal
          preview={mileagePreview}
          sourceType={mileagePrompt.sourceType}
          sourceReferenceId={mileagePrompt.sourceReferenceId}
          onClose={() => {
            setMileagePrompt(null);
            setMileagePreview(null);
          }}
        />
      )}
      {hasWorkspaceSession && !isPublicStandalonePage && <NotificationPermissionBanner />}
      {isMidwifeUser && !isPublicStandalonePage && <BackToLaborButton />}
      <ErrorBoundary>
        <AppRoutes />
      </ErrorBoundary>
    </>
  );
}

export function SessionApp() {
  const { user, isAuthenticated } = useAuth();
  if (isBillingContext(user)) return <BillingWorkspaceShell />;
  const practiceServicesEnabled = Boolean(isAuthenticated && user
    && !isGlobalAdminContext(user) && !isContextSelectionRequired(user));
  return <SubscriptionProvider enabled={practiceServicesEnabled}>
            <AppointmentTypeCatalogProvider enabled={practiceServicesEnabled}>
              <NotificationProvider>
                <SystemNotificationProvider enabled={practiceServicesEnabled}>
                  <ServiceWorkerProvider>
                    <MessageProvider enabled={practiceServicesEnabled}>
                      <AppContent />
                    </MessageProvider>
                  </ServiceWorkerProvider>
                </SystemNotificationProvider>
              </NotificationProvider>
            </AppointmentTypeCatalogProvider>
          </SubscriptionProvider>;
}

function App() {
  return (
    <Router>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <SessionApp />
        </AuthProvider>
      </QueryClientProvider>
    </Router>
  );
}

export default App;
